import { ContentState, convertFromRaw, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { DecimalRegex } from '../constants';
import { TimeAMPM } from '../enums';
import { CoreInputModelUtil } from './CoreInputModelUtil';

export class UtilityMethods {
  static months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  static days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  static fixNumber = (x: number) => {
    return (Math.round(x * 100) / 100).toFixed(2);
  };

  static numberWithCommas(x: number, needToFixed = false) {
    const value = needToFixed ? this.fixNumber(x) : x;
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  static convertTo2Decimals = (x: number) => {
    return Number((Math.round(x * 100) / 100).toFixed(2));
  };

  static formatDateInString(date: Date): string {
    const month = date.getMonth();
    const day = date.getDate();
    const year = date.getFullYear();
    return `${this.months[month]} ${day}, ${year}`;
  }

  static formatDateInMonthAndDay(date: Date): string {
    const month = date.getMonth();
    const day = date.getDate();
    return `${this.months[month]} ${day}`;
  }

  static formatMMDDYYYY(date: Date): string {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  static isValidObjectId(id: string) {
    return id.length === 24 && /^[A-F0-9]+$/i.test(id);
  }

  static toLocalISOString(date: Date): string {
    const offset = date.getTimezoneOffset() * 60000; // Offset in milliseconds
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().slice(0, 19); // Remove "Z" at the end
  }

  static removeZFromDateString(date: string): Date {
    if (!date) return new Date();
    // return new Date(date);
    // TO DO until we have timze one comment below line
    return new Date(date?.replace('Z', ''));
  }

  static formatAMPM(date: Date) {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minuteStr = minutes < 10 ? '0' + minutes : minutes;
    const strTime = hours + ':' + minuteStr + ' ' + ampm;
    return strTime;
  }

  static validateDecimalValue(value: string, validateGreaterThan0 = false): boolean {
    if (!value) {
      return false;
    }

    if (!DecimalRegex.test(value)) {
      return false;
    }

    if (validateGreaterThan0 && parseFloat(value) <= 0) {
      return false;
    }

    return true;
  }

  static formatDateInStringWithTime(date: Date): string {
    return this.formatDateInString(date) + ', ' + this.formatAMPM(date);
  }

  static removeSpecialCharacters(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, '');
  }

  static uniqueId(length: number) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  }

  static getDayCommaMonthDate(date: Date): string {
    return `${this.days[date.getDay()].toUpperCase()}, ${this.months[date.getMonth()]} ${date.getDate()}`;
  }

  static getDayCommaMonthDateCommaYear(date: Date, showCurrentYear = true): string {
    const fullYear = date.getFullYear();
    const year = !showCurrentYear ? (fullYear === new Date().getFullYear() ? '' : fullYear) : fullYear;
    return `${this.days[date.getDay()]}, ${this.months[date.getMonth()]} ${date.getDate()}${year ? `, ${year}` : ''}`;
  }

  static getEditorStateFromPlainText(plainText: string) {
    plainText = !plainText ? '' : plainText;
    const contentState = ContentState.createFromText(plainText);
    return EditorState.createWithContent(contentState);
  }

  static onlyUnique(value: string, index: number, array: any[]) {
    return array.indexOf(value) === index;
  }

  //get draft js editor html
  static getDraftJsEditorHtml(editorState: EditorState | string): string {
    try {
      if (typeof editorState === 'string') {
        const rawContent = JSON.parse(editorState);
        const contentState = convertFromRaw(rawContent);
        return draftToHtml(convertToRaw(contentState));
      }

      if (editorState instanceof EditorState) {
        return draftToHtml(convertToRaw(editorState.getCurrentContent()));
      }

      return '';
    } catch (error) {
      console.error('Error converting editor state to HTML:', error);
      return '';
    }
  }

  static formatTaxId = (value: string): string => {
    const sanitizedValue = value.replace(/\D/g, '');
    if (sanitizedValue.length === 0) {
      return '';
    }
    if (sanitizedValue.length <= 1) {
      return sanitizedValue;
    }
    const firstPart = sanitizedValue.slice(0, 2);
    const secondPart = sanitizedValue.slice(2, 9);

    return `${firstPart}-${secondPart}`;
  };

  static parseEditorState(rawContent?: string): EditorState {
    if (!rawContent) {
      return EditorState.createEmpty();
    }
    if (CoreInputModelUtil.isValidJSON(rawContent)) {
      return EditorState.createWithContent(convertFromRaw(JSON.parse(rawContent)));
    }
    return EditorState.createWithContent(ContentState.createFromText(rawContent));
  }

  static calculatePercentageValue(total: number, percentage: number) {
    return (total * percentage) / 100;
  }

  static getEditorHtmlContent(editorRawContent: string) {
    let editorState: EditorState | undefined = undefined;
    if (editorRawContent) {
      try {
        const v = editorRawContent?.toString();
        const es = convertFromRaw(JSON.parse(v));
        editorState = EditorState.createWithContent(es);
      } catch (e) {}
    }

    if (!editorState) {
      return editorRawContent;
    }
    return UtilityMethods.getDraftJsEditorHtml(editorState);
  }

  static combineDateAndTime(date: string, time: string): string {
    const dateObj = UtilityMethods.removeZFromDateString(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const localDateString = `${year}-${month}-${day}`;
    const dateTimeString = `${localDateString}T${time}`;
    return dateTimeString;
  }

  static addTimeToDate(date: Date, time: string, amPm: keyof typeof TimeAMPM): Date {
    // Split the time string into hours and minutes
    const [hours, minutes] = time.split(':').map(Number);

    // Adjust hours based on AM/PM
    let adjustedHours = hours;
    if (amPm === 'PM' && hours < 12) {
      adjustedHours += 12;
    } else if (amPm === 'AM' && hours === 12) {
      adjustedHours = 0;
    }

    const newDate = new Date(date);
    newDate.setHours(adjustedHours, minutes, 0, 0);

    const offset = newDate.getTimezoneOffset() * 60000; // offset in milliseconds
    return new Date(newDate.getTime() - offset);
  }

  static addTimeToDateWithoutOffset(date: Date, time: string, amPm: keyof typeof TimeAMPM): Date {
    // Split the time string into hours and minutes
    const [hours, minutes] = time.split(':').map(Number);

    // Adjust hours based on AM/PM
    let adjustedHours = hours;
    if (amPm === 'PM' && hours < 12) {
      adjustedHours += 12;
    } else if (amPm === 'AM' && hours === 12) {
      adjustedHours = 0;
    }

    // Create a new Date object with the same date but updated time
    const newDate = new Date(date);
    newDate.setHours(adjustedHours, minutes, 0, 0);

    return newDate;
  }

  static getHHMMFromDate(date: Date): string {
    // Get hours and minutes from the date
    let hours = date.getHours();
    const minutes = date.getMinutes();
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    // Format hours and minutes to ensure two digits
    const hourStr = hours < 10 ? '0' + hours : hours;
    const minuteStr = minutes < 10 ? '0' + minutes : minutes;

    return `${hourStr}:${minuteStr}`;
  }

  static getAMPMFromDate(date: Date) {
    const hours = date.getHours();
    return hours >= 12 ? 'PM' : 'AM';
  }

  static getMonth(number: string): string {
    const month = parseInt(number);
    switch (month) {
      case 1:
        return 'Jan';
      case 2:
        return 'Feb';
      case 3:
        return 'Mar';
      case 4:
        return 'Apr';
      case 5:
        return 'May';
      case 6:
        return 'Jun';
      case 7:
        return 'Jul';
      case 8:
        return 'Aug';
      case 9:
        return 'Sep';
      case 10:
        return 'Oct';
      case 11:
        return 'Nov';
      case 12:
        return 'Dec';
      default:
        return '';
    }
  }

  static getISODate = (date: Date) => {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
  };

  static getUtcDate = (date: Date) => {
    // return new Date(
    //     Date.UTC(
    //         date.getFullYear(),
    //         date.getMonth(),
    //         date.getDate(),
    //         date.getHours(),
    //         date.getMinutes(),
    //         date.getSeconds(),
    //     ),
    // );
    return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  };

  static calculateMarginPercentage(price: number, cost: number) {
    if (price === 0 || cost === 0) {
      return 0;
    }
    const margin = (price / cost) * 100;

    return Math.round(margin * 100) / 100;
  }

  static getPaymentSiteURL() {
    return window.location.origin;
  }

  static formatHourAMPM(date: Date) {
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    const strTime = hours + ' ' + ampm;
    return strTime;
  }

  static formatDecimalNumber(num: number) {
    // Convert the number to a string
    const str = num.toString();

    // Check if the number has a decimal part
    if (str.includes('.')) {
      // Split the number into integer and decimal parts
      const parts = str.split('.');

      // If the decimal part is '00', return the integer part
      if (parts[1] === '00') {
        return parseInt(parts[0]);
      } else {
        // Otherwise, return the number as is
        return parseFloat(num.toFixed(2));
      }
    } else {
      // If there's no decimal part, return the number as is
      return num;
    }
  }

  static numberToWord(num: number): string {
    const words = [
      'Zero',
      'One',
      'Two',
      'Three',
      'Four',
      'Five',
      'Six',
      'Seven',
      'Eight',
      'Nine',
      'Ten',
      'Eleven',
      'Twelve',
      'Thirteen',
      'Fourteen',
      'Fifteen',
      'Sixteen',
      'Seventeen',
      'Eighteen',
      'Nineteen',
    ];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    if (num < 20) return words[num];
    if (num < 100) {
      const tenIndex = Math.floor(num / 10);
      const remainder = num % 10;
      return tens[tenIndex] + (remainder > 0 ? ` ${words[remainder]}` : '');
    }
    if (num < 1000) {
      const hundredIndex = Math.floor(num / 100);
      const remainder = num % 100;
      return words[hundredIndex] + ' Hundred' + (remainder > 0 ? ` ${this.numberToWord(remainder)}` : '');
    }
    return 'Number out of range';
  }
  static removeLastS(value: string) {
    return value.replace(/s$/, '');
  }
}
